import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardService from '../services/dashboardService';
import Icons from '../components/Icons';
import api from '../services/api';
import AdminCalendarManager from '../components/AdminCalendarManager';
import AdminDocumentManager from '../components/AdminDocumentManager';
import AdminTaskManager from '../components/AdminTaskManager';
import AdminVolunteerManager from '../components/AdminVolunteerManager';
import AdminAnnouncementsManager from '../components/AdminAnnouncementsManager';
import NotificationsList from '../components/NotificationsList';
import AdminProfile from '../components/AdminProfile';
import OrganizationSwitcher from '../components/OrganizationSwitcher';
import NotificationBanner from '../components/NotificationBanner';
import NotificationBell from '../components/NotificationBell';
import ViewAsBanner from '../components/ViewAsBanner';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/tasks')) return 'tasks';
    if (path.includes('/events')) return 'events';
    if (path.includes('/documents')) return 'documents';
    if (path.includes('/volunteers')) return 'volunteers';
    if (path.includes('/announcements')) return 'announcements';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/profile')) return 'profile';
    return 'overview';
  };

  const activeTab = getActiveTab();

  // Helper function to get proper volunteer name from various data formats
  const getVolunteerDisplayName = (activity) => {
    // Try assignee_first_name/assignee_last_name from activity
    if (activity.assignee_first_name && activity.assignee_first_name.trim()) {
      let name = activity.assignee_first_name.trim();
      if (activity.assignee_last_name && activity.assignee_last_name.trim()) {
        name += ` ${activity.assignee_last_name.trim()}`;
      }
      return name;
    }

    // Fallback to email if no proper name is available
    if (activity.assignee_email) {
      return activity.assignee_email;
    }

    return 'Someone';
  };

  // Helper function to format activity data
  const formatActivityItem = (activity) => {
    console.log('Formatting activity:', activity);
    console.log('Due date value:', activity.due_date);
    console.log('Due date type:', typeof activity.due_date);

    if (!activity) {
      return { description: 'No activity data', timestamp: 'Unknown', status: 'unknown' };
    }

    let description = '';

    // Check if this is a task assignment (from dashboard controller)
    if (activity.activity_type === 'task_assigned') {
      const assigneeName = getVolunteerDisplayName(activity);
      description = `Task "${activity.title || 'Untitled'}" assigned to ${assigneeName}`;
      console.log('Assignment description:', description);
    }
    // Check if this is from admin route (different structure)
    else if (activity.type === 'task_assignment') {
      description = `Task "${activity.description || 'Untitled'}" assigned to ${activity.user_name || 'Someone'}`;
      console.log('Admin route assignment description:', description);
    }
    else {
      // Fallback for other activity types
      description = activity.description || activity.title || 'Activity occurred';
      console.log('Other activity description:', description);
    }

    // Set status first to check if completed
    let status = activity.status || 'assigned';

    // Check if task is overdue (only for incomplete tasks)
    const isOverdue = activity.due_date ? (() => {
      const [year, month, day] = activity.due_date.split('T')[0].split('-');
      const dueDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return dueDate < today;
    })() : false;
    if (isOverdue && status !== 'completed') {
      status = 'overdue';
    }

    // Format due date based on status
    const formatDueDate = (dateString, taskStatus) => {
      if (!dateString) return 'No due date';
      // Parse as local date to avoid timezone shift
      const [year, month, day] = dateString.split('T')[0].split('-');
      const date = new Date(year, month - 1, day);

      // If task is completed, just show the completion date
      if (taskStatus === 'completed') {
        return `Completed ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      }

      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const diffTime = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
      if (diffDays === 0) return 'Due today';
      if (diffDays === 1) return 'Due tomorrow';
      if (diffDays <= 7) return `Due in ${diffDays} days`;
      return `Due ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    };

    // Always prioritize due_date over created_at for timestamp
    const timestamp = activity.due_date
      ? formatDueDate(activity.due_date, status)
      : 'No due date';

    return { description, timestamp, status };
  };

  // Helper function to format time from 24-hour to 12-hour format
  const formatTime12Hour = (timeString) => {
    if (!timeString) return '';

    // Parse the time string (assuming format like "14:30:00" or "14:30")
    const timeParts = timeString.split(':');
    let hours = parseInt(timeParts[0]);
    const minutes = timeParts[1] || '00';

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    return `${hours}:${minutes} ${ampm}`;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const result = await DashboardService.getAdminDashboard();
      console.log('Admin Dashboard Data:', result.data);
      console.log('Recent Activity:', result.data?.recentActivity);
      if (result.data?.recentActivity?.length > 0) {
        console.log('First Activity Item:', result.data.recentActivity[0]);
        console.log('First Activity due_date field:', result.data.recentActivity[0].due_date);
        console.log('First Activity due_date type:', typeof result.data.recentActivity[0].due_date);
        console.log('Raw JSON of first activity:', JSON.stringify(result.data.recentActivity[0], null, 2));
      }
      setDashboardData(result.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const response = await api.get('/documents');
      if (response.data.success) {
        // Get the 6 most recent documents for dashboard
        const recent = response.data.documents.slice(0, 6);
        setRecentDocuments(recent);
        console.log('Recent Documents:', recent);
        if (recent.length > 0) {
          console.log('First Document Item:', recent[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Don't set main error for documents - they're secondary
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchRecentDocuments();

    // Redirect base admin-dashboard to overview
    if (location.pathname === '/admin-dashboard') {
      navigate('/admin-dashboard/overview', { replace: true });
    }
  }, [location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <ViewAsBanner />
      <div className="flex-1 flex overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-sm border-b z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-900 truncate mx-2 min-w-0">{user?.organizationName || 'Admin Dashboard'}</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <NotificationBell isAdmin={true} />
            <button
              onClick={logout}
              className="text-red-500 hover:text-red-700 p-1"
              title="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-30
        lg:static lg:inset-auto
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
        h-screen
      `}>
        {/* Mobile: Top spacing for header */}
        <div className="h-16 lg:hidden flex-shrink-0"></div>

        {/* Organization Name - Desktop Only */}
        <div className="hidden lg:flex flex-shrink-0 border-b border-gray-200 p-4 items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {user?.organizationName || 'Admin Portal'}
            </h1>
            <p className="text-xs text-gray-500 mt-1">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</p>
          </div>
          <div className="flex-shrink-0">
            <NotificationBell isAdmin={true} />
          </div>
        </div>

        {/* Organization Switcher */}
        <div className="flex-shrink-0">
          <OrganizationSwitcher />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {[
            { id: 'overview', label: 'Overview', route: '/admin-dashboard/overview', icon: Icons.chart },
            { id: 'tasks', label: 'Task Management', route: '/admin-dashboard/tasks', icon: Icons.clipboard },
            { id: 'events', label: 'Event Management', route: '/admin-dashboard/events', icon: Icons.calendar },
            { id: 'documents', label: 'Document Management', route: '/admin-dashboard/documents', icon: Icons.doc },
            { id: 'volunteers', label: 'Volunteer Management', route: '/admin-dashboard/volunteers', icon: Icons.users },
            { id: 'announcements', label: 'Announcements', route: '/admin-dashboard/announcements', icon: Icons.megaphone },
            { id: 'notifications', label: 'Notifications', route: '/admin-dashboard/notifications', icon: Icons.bell },
            { id: 'profile', label: 'Organization Profile', route: '/admin-dashboard/profile', icon: Icons.user },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => { navigate(item.route); setSidebarOpen(false); }}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors flex items-center space-x-3 ${
                activeTab === item.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className={activeTab === item.id ? 'text-blue-600' : 'text-gray-400'}>
                {item.icon("w-4 h-4")}
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4 pb-8 lg:pb-4 bg-white">
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-900">
              {user?.firstName || 'Admin'} {user?.lastName || ''}
            </p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="w-full bg-danger-500 hover:bg-danger-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Welcome Section */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.firstName || 'Admin'}!
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                Here's what's happening in your organization
              </p>
            </div>

            {/* Notification Banner */}
            <NotificationBanner />

            {/* Quick Stats - Admin Version */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-lg shadow p-5">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-50">
                    <span className="text-blue-600">{Icons.users("w-5 h-5")}</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Total Volunteers</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboardData?.volunteerStats?.total_volunteers || '0'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-5">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-50">
                    <span className="text-yellow-600">{Icons.clipboard("w-5 h-5")}</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Active Tasks</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboardData?.taskOverview?.pending_tasks || '0'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-5">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-50">
                    <span className="text-green-600">{Icons.check("w-5 h-5")}</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Completed Tasks</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboardData?.taskOverview?.completed_assignments || '0'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-5">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-50">
                    <span className="text-purple-600">{Icons.calendar("w-5 h-5")}</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Upcoming Events</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboardData?.upcomingEvents?.length || '0'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              {/* Recent Activity Section */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400">{Icons.bolt("w-4 h-4")}</span>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">Recent Activity</h3>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  {dashboardData?.recentActivity?.length > 0 ? (
                    <div className="space-y-4">
                      {dashboardData.recentActivity.slice(0, 8).map((activity, index) => {
                        console.log(`Activity ${index}:`, activity);
                        const formattedActivity = formatActivityItem(activity);
                        console.log(`Formatted Activity ${index}:`, formattedActivity);

                        const statusColor = formattedActivity.status === 'completed' ? 'bg-green-50 text-green-800' :
                                          formattedActivity.status === 'in_progress' ? 'bg-blue-50 text-blue-800' :
                                          formattedActivity.status === 'overdue' ? 'bg-red-50 text-red-800' :
                                          'bg-yellow-50 text-yellow-800';

                        return (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <span className={
                                formattedActivity.status === 'completed' ? 'text-green-500' :
                                formattedActivity.status === 'in_progress' ? 'text-blue-500' :
                                formattedActivity.status === 'overdue' ? 'text-red-500' : 'text-yellow-500'
                              }>
                                {formattedActivity.status === 'completed' ? Icons.check("w-4 h-4") :
                                 formattedActivity.status === 'overdue' ? Icons.alertTriangle("w-4 h-4") :
                                 Icons.clipboard("w-4 h-4")}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900">{formattedActivity.description}</p>
                                  <p className="text-xs text-gray-500">{formattedActivity.timestamp}</p>
                                </div>
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${statusColor} ml-2`}>
                                  {formattedActivity.status === 'in_progress' ? 'in progress' : formattedActivity.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No recent activity</h4>
                      <p className="text-gray-600 mb-4">
                        Activity will appear here when you assign tasks to volunteers
                      </p>
                      <button
                        onClick={() => navigate('/admin-dashboard/tasks')}
                        className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Create Your First Task
                      </button>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t">
                    <button
                      onClick={() => navigate('/admin-dashboard/tasks')}
                      className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                    >
                      View all activity →
                    </button>
                  </div>
                </div>
              </div>

              {/* Upcoming Events Section */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400">{Icons.calendar("w-4 h-4")}</span>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">Upcoming Events</h3>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  {dashboardData?.upcomingEvents?.length > 0 ? (
                    <div className="space-y-4">
                      {dashboardData.upcomingEvents.slice(0, 3).map((event, index) => (
                        <div key={index} className="border-l-4 border-purple-400 pl-4">
                          <h4 className="font-medium text-gray-900">{event.title}</h4>
                          <p className="text-sm text-gray-600">{event.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(() => {
                              const [year, month, day] = event.start_date.split('T')[0].split('-');
                              const date = new Date(year, month - 1, day);
                              return date.toLocaleDateString();
                            })()} at {formatTime12Hour(event.start_time)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No upcoming events</h4>
                      <p className="text-gray-600 mb-4">
                        Create events to keep your volunteers informed and engaged
                      </p>
                      <button
                        onClick={() => navigate('/admin-dashboard/events')}
                        className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Schedule Your First Event
                      </button>
                    </div>
                  )}

                  <div className="mt-6 pt-4 border-t">
                    <button
                      onClick={() => navigate('/admin-dashboard/events')}
                      className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                    >
                      View all events →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400">{Icons.doc("w-4 h-4")}</span>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">Recent Documents</h3>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                {documentsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading documents...</p>
                  </div>
                ) : recentDocuments?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recentDocuments.map((doc, index) => (
                      <div key={doc.id || index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                              {doc.is_pinned ? Icons.pin("w-4 h-4 text-yellow-500") : Icons.doc("w-4 h-4 text-blue-500")}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900 truncate">
                                {doc.title}
                              </h4>
                              {doc.is_pinned && (
                                <span className="text-xs text-yellow-500 font-medium">Pinned</span>
                              )}
                            </div>
                            {doc.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {doc.description}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {(() => {
                                  const [year, month, day] = doc.created_at.split('T')[0].split('-');
                                  const date = new Date(year, month - 1, day);
                                  return date.toLocaleDateString();
                                })()}
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                {doc.category}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h4>
                    <p className="text-gray-600 mb-4">
                      Upload documents to share them with your volunteers
                    </p>
                    <button
                      onClick={() => navigate('/admin-dashboard/documents')}
                      className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Upload Documents
                    </button>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => navigate('/admin-dashboard/documents')}
                    className="text-blue-500 hover:text-blue-600 text-sm font-medium"
                  >
                    View all documents →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <AdminTaskManager />
        )}

        {activeTab === 'events' && (
          <AdminCalendarManager />
        )}

        {activeTab === 'documents' && (
          <AdminDocumentManager />
        )}

        {activeTab === 'volunteers' && (
          <AdminVolunteerManager
            dashboardData={dashboardData}
            onRefreshData={fetchDashboardData}
          />
        )}

        {activeTab === 'announcements' && (
          <AdminAnnouncementsManager />
        )}

        {activeTab === 'notifications' && (
          <NotificationsList isAdmin={true} includeAnnouncements={false} />
        )}

        {activeTab === 'profile' && (
          <AdminProfile />
        )}
        </div>
      </main>
      </div>
    </div>
  );
};

export default AdminDashboard;